/**
 * Referee Token Conversion Contracts
 *
 * DATABASE-FIRST: Matches referee_token_conversions table + stored procedures
 * Source: MCP query on 2026-01-02
 *
 * Table: referee_token_conversions (10 columns)
 *   id: integer (PK, serial)
 *   referee_identifier: varchar NOT NULL
 *   amount: numeric NOT NULL
 *   bonus_amount: numeric DEFAULT 0
 *   bonus_percent: integer DEFAULT 0
 *   total_credited: numeric (nullable - set on completion)
 *   status: varchar DEFAULT 'pending' (CHECK: 'pending' | 'completed' | 'failed')
 *   completed_at: timestamptz (nullable)
 *   created_at: timestamptz DEFAULT now()
 *   target_telegram_id: bigint (nullable - set on completion)
 *
 * Functions:
 *   create_referee_token_conversion(p_referee_identifier varchar, p_amount numeric)
 *   RETURNS TABLE(success boolean, message text, conversion_id integer, bonus_amount numeric, total_credited numeric)
 *
 *   complete_referee_token_conversion_v2(p_conversion_id integer, p_target_telegram_id bigint, p_total_credited numeric)
 *   RETURNS boolean
 */
import { z } from 'zod';
import { successResponseSchema, timestampStringRequired, timestampString } from '../common.js';
// =============================================================================
// CONVERSION STATUS (CHECK constraint)
// =============================================================================
/**
 * Valid conversion statuses from CHECK constraint 'valid_conversion_status'
 * CHECK ((status)::text = ANY (ARRAY['pending', 'completed', 'failed']))
 */
export const refereeConversionStatusValues = ['pending', 'completed', 'failed'];
export const refereeConversionStatusSchema = z.enum(refereeConversionStatusValues);
// =============================================================================
// REQUEST SCHEMA
// =============================================================================
/**
 * POST /api/referee/token-conversion request body
 * Matches backend TokenConversionRequestSchema
 */
export const tokenConversionRequestSchema = z.object({
    amount: z.number().positive().max(100000),
    targetTelegramId: z.string().min(5).max(20), // Telegram ID as string
});
// =============================================================================
// STORED PROCEDURE RESULT
// =============================================================================
/**
 * create_referee_token_conversion() return type
 */
export const createTokenConversionResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    conversion_id: z.number().int().nullable(), // Only present on success
    bonus_amount: z.number().nullable(),
    total_credited: z.number().nullable(),
});
// =============================================================================
// API RESPONSES
// =============================================================================
/**
 * POST /api/referee/token-conversion response data (inner data, for frontend consumption)
 * Includes bonus_percent which is queried separately in controller
 */
export const tokenConversionResponseDataSchema = z.object({
    message: z.string(),
    conversionId: z.number().int(),
    amount: z.number(),
    bonusAmount: z.number(),
    bonusPercent: z.number().int(),
    totalCredited: z.number(),
    targetTelegramId: z.string(),
    targetUsername: z.string().nullable(),
});
/**
 * POST /api/referee/token-conversion full response (with success wrapper)
 */
export const tokenConversionResponseSchema = successResponseSchema(tokenConversionResponseDataSchema);
/**
 * Single conversion entry
 * Used in GET /api/referee/conversions list
 */
export const refereeConversionSchema = z.object({
    id: z.number().int().positive(),
    amount: z.number(),
    bonusAmount: z.number(),
    bonusPercent: z.number().int(),
    totalCredited: z.number().nullable(),
    status: refereeConversionStatusSchema,
    targetTelegramId: z.string().nullable(), // bigint → string for JSON safety
    targetUsername: z.string().nullable(), // From JOIN with users table
    completedAt: timestampString,
    createdAt: timestampStringRequired,
});
/**
 * GET /api/referee/conversions response data (inner data, for frontend consumption)
 */
export const refereeConversionsResponseDataSchema = z.object({
    conversions: z.array(refereeConversionSchema),
    total: z.number().int().nonnegative(),
});
/**
 * GET /api/referee/conversions full response (with success wrapper)
 */
export const refereeConversionsResponseSchema = successResponseSchema(refereeConversionsResponseDataSchema);
// =============================================================================
// QUERY PARAMS
// =============================================================================
/**
 * Conversions list query parameters
 */
export const refereeConversionsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
//# sourceMappingURL=conversion.js.map